import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button'; // Keep this import
import { Trash, Plus, User, Users, Pencil, Search, Edit, UserCog, Import, UserPlus, Download, LucideUserPlus, LucideUsers2, Check, X, ChevronDown, ChevronLeft, FileCode, UserMinus, Loader2, List, GraduationCap, Building2, UserCheck, School, Group, Users2, UserCheck2, UserRoundCog, UserCog2, Mail, RefreshCw, Upload, FolderSync, FileUserIcon } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from '@/contexts/LanguageContext';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import AddStudentForm from '@/components/agenda/AddStudentForm';
import EditStudentForm from '@/components/agenda/EditStudentForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { BASE_URL } from '@/config/api';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from '@/components/ui/pagination';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import ImportStudentsModal from '@/components/settings/ImportStudentsModal';
import { utils, write } from 'xlsx';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import DuplicateStudentsDetector from '@/components/settings/DuplicateStudentsDetector';
import { useQueryClient } from '@tanstack/react-query';
import { eventService } from '@/services/eventService';
import { ContactStudentModal } from '@/components/agenda/ContactStudentModal'; // Import the ContactStudentModal

interface Student {
  id: string;
  name?: string;
  firstname: string;
  schools: any;
  school_id: string;
  has_user_account: boolean;
  lastname: string;
  email?: string;
  phone?: string;
  mobile?: string;
  mother_email?: string;
  mother_phone?: string;
  father_email?: string;
  father_phone?: string;
  profile_picture?: string;
  school_origin?: string;
  status: string;
  families?: Array<{
    id: number;
    firstname: string;
    email: string;
    isUserAccount: boolean;
  }>;
}

interface DuplicateStudentsDetectorHandle {
  openModal: () => void;
  openMergeModal: (student: Student) => void;
}


interface Family {
  id: number;
  firstname: string;
  email: string;
  isUserAccount: boolean;
  students: Array<{
    id: string;
    firstname: string;
    lastname: string;
  }>;
  studentCount: number;
}

interface School {
  id: string;
  school_name: string;
  school_type: 'S' | 'C';
}

interface StudentsManagerProps {
  small?: boolean;
  onStudentsLoad?: (count: number) => void;
}

//const STUDENTS_PER_PAGE = 10;

const StudentsManager: React.FC<StudentsManagerProps> = ({ small = false, onStudentsLoad }) => {
  const navigate = useNavigate();
  const { user, token, refreshUserData } = useAuth();
  const { t } = useLanguage();
  const [students, setStudents] = useState<Student[]>([]);
  const [families, setFamilies] = useState<Family[]>([]);
  const [isAddingStudent, setIsAddingStudent] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(1);
  const [isLoadingStudents, setIsLoadingStudents] = useState(true);
  const [isDuplicateReady, setIsDuplicateReady] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importedData, setImportedData] = useState(null);
  const [fieldMappings, setFieldMappings] = useState<{ [key: string]: string }>({});
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const [isViewingFamilies, setIsViewingFamilies] = useState(false);
  const [familySearchQuery, setFamilySearchQuery] = useState('');
  const [selectedFamily, setSelectedFamily] = useState<Family | null>(null);
  const [isFamilyModalOpen, setIsFamilyModalOpen] = useState(false);
  const [isCreateFamilyModalOpen, setIsCreateFamilyModalOpen] = useState(false);
  const [newFamilyName, setNewFamilyName] = useState('');
  const [newFamilyEmail, setNewFamilyEmail] = useState('');
  const [selectedStudentsForFamily, setSelectedStudentsForFamily] = useState<string[]>([]);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [hasNameChanged, setHasNameChanged] = useState(false);
  const [hasEmailChanged, setHasEmailChanged] = useState(false);
  const [initialFamily, setInitialFamily] = useState<Family | null>(null);
  const [selectedStudentsToAdd, setSelectedStudentsToAdd] = useState<string[]>([]);
  const [showAddStudentsList, setShowAddStudentsList] = useState(false);
  const [isSingleDeleteConfirmOpen, setIsSingleDeleteConfirmOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<Student | null>(null);
  const [duplicatesCount, setDuplicatesCount] = useState(0);
  const [duplicatesDetected, setDuplicatesDetected] = useState(false);
  const [isDuplicateLoading, setisDuplicateLoading] = useState(false);
  const [studentsPerPage, setStudentsPerPage] = useState<number>(10);

  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [isLoadingAllStudents, setIsLoadingAllStudents] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [selectedSchoolId, setSelectedSchoolId] = useState<string>(user.school_id);
  const [userSchools, setUserSchools] = useState<School[]>([]);

  const [isFamilyDeleteConfirmOpen, setIsFamilyDeleteConfirmOpen] = useState(false);
  const [familyToDelete, setFamilyToDelete] = useState<Family | null>(null);

  const [isFamilyInvited, setIsFamilyInvited] = useState(false);
  const queryClient = useQueryClient();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false); // State for contact modal
  const [selectedStudentForContact, setSelectedStudentForContact] = useState<Student | null>(null); // State for student to contact
  const [shouldReopenEditModal, setShouldReopenEditModal] = useState(false);

  const [editKey, setEditKey] = useState(0);

  // Check if user is a coach with teachers_all role
  const isTeachersAllCoach = user?.account_type === "C" && user?.roleType === "teachers_all";


  useEffect(() => {
    if (user?.schools && user.schools.length > 0) {
      setUserSchools(user.schools);
    }
  }, [user]);

  useEffect(() => {
    if (selectedFamily) {
      setInitialFamily({ ...selectedFamily });
    }
  }, [selectedFamily]);

  useEffect(() => {
    if (selectedFamily && initialFamily) {
      setHasNameChanged(selectedFamily.firstname !== initialFamily.firstname);
      setHasEmailChanged(selectedFamily.email !== initialFamily.email);
    }
  }, [selectedFamily, initialFamily]);

  const fetchStudents = useCallback(async (schoolId: string, page: number, perPage: number, search: string) => {
    setIsLoadingStudents(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      if (search) {
        params.append('search', search);
      }

      const response = await fetch(`${BASE_URL}/api/students/school/${schoolId}?${params.toString()}`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setStudents(data.students || []);
        setTotalPages(data.last_page || 1);
        setTotalResults(data.total || 0);
        setFamilies(data.families || []);
        if (onStudentsLoad) {
          onStudentsLoad(data.total || 0);
        }
        setTimeout(() => {
          setIsDuplicateReady(true);
        }, 2000);
        return data.students || [];
      } else {
          toast({
            title: "Failed to fetch students",
            description: "There was an error loading the students list.",
            variant: "destructive"
          });
          return [];
      }
    } catch (error) {
        console.error('Error fetching students:', error);
        toast({
          title: "Failed to fetch students",
          description: "There was an error loading the students list.",
          variant: "destructive"
        });
        return [];
    } finally {
        setIsLoadingStudents(false);
    }
  }, [token, toast, onStudentsLoad]);

  const fetchAllStudents = async (): Promise<Student[]> => {
    if (allStudents.length > 0) {
      return allStudents;
    }
    setIsLoadingAllStudents(true);
    try {
      const response = await fetch(`${BASE_URL}/api/agenda/students`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        const fetchedStudents = data.students || [];
        setAllStudents(fetchedStudents);
        return fetchedStudents;
      } else {
          toast({
            title: "Failed to fetch all students",
            description: "There was an error loading the complete students list.",
            variant: "destructive"
          });
          return [];
      }
    } catch (error) {
        console.error('Error fetching all students:', error);
        toast({
          title: "Failed to fetch all students",
          description: "There was an error loading the complete students list.",
          variant: "destructive"
        });
        return [];
    } finally {
        setIsLoadingAllStudents(false);
    }
  };

  const handleStudentRefresh = useCallback(() => {
    fetchStudents(selectedSchoolId, currentPage, studentsPerPage, debouncedSearchQuery);
  }, [fetchStudents, selectedSchoolId, currentPage, studentsPerPage, debouncedSearchQuery]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  useEffect(() => {
    if (debouncedSearchQuery !== searchQuery) {
      setCurrentPage(1);
    }
  }, [debouncedSearchQuery, searchQuery]);

  useEffect(() => {
    if (selectedSchoolId) {
      fetchStudents(selectedSchoolId, currentPage, studentsPerPage, debouncedSearchQuery);
    }
  }, [selectedSchoolId, currentPage, studentsPerPage, debouncedSearchQuery, fetchStudents]);

  useEffect(() => {
    if (!isLoadingStudents && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isLoadingStudents]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedFamily) {
      setSelectedFamily({ ...selectedFamily, firstname: e.target.value });
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedFamily) {
      setSelectedFamily({ ...selectedFamily, email: e.target.value });
    }
  };

  const deleteFamily = async (familyId: number) => {
    const family = families.find(f => f.id === familyId);
    if (family) {
      setFamilyToDelete(family);
      setIsFamilyDeleteConfirmOpen(true);
    }
  };


  const confirmDeleteFamily = async () => {
    if (!familyToDelete) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/students/family/delete`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id: familyToDelete.id })
      });
      if (!response.ok) {
        throw new Error('Failed to delete family');
      }
      fetchStudents(selectedSchoolId, currentPage, studentsPerPage, debouncedSearchQuery);
      toast({
        title: "Family deleted",
        description: `The family has been deleted successfully.`,
      });
    } catch (error) {
      console.error('Error deleting family:', error);
      toast({
        title: "Failed to delete family",
        description: "There was an error deleting the family. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
      setIsFamilyDeleteConfirmOpen(false);
      setFamilyToDelete(null);
    }
  };


  const handleAddStudentsToFamily = async () => {
  if (!selectedFamily || selectedStudentsToAdd.length === 0) return;
  setIsLoading(true);
  try {
    const response = await fetch(`${BASE_URL}/api/students/families/add-students`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        familyId: selectedFamily.id,
        studentIds: selectedStudentsToAdd
      })
    });

    if (!response.ok) {
      throw new Error('Failed to add students to family');
    }
    
    setShowAddStudentsList(false);

    const addedStudents = allStudents.filter(student => selectedStudentsToAdd.includes(student.id));

    // ✅ Correction ici : si selectedFamily.students n'existe pas, on prend un tableau vide
    const updatedFamily = {
      ...selectedFamily,
      students: [...(selectedFamily.students || []), ...addedStudents]
    };

    setSelectedFamily(updatedFamily);
    fetchStudents(selectedSchoolId, currentPage, studentsPerPage, debouncedSearchQuery);
    setSelectedStudentsToAdd([]);

    toast({
      title: "Students added to family",
      description: "The students have been added to the family successfully."
    });
  } catch (error) {
    console.error('Error adding students to family:', error);
    toast({
      title: "Failed to add students to family",
      description: "There was an error adding the students to the family. Please try again.",
      variant: "destructive"
    });
  } finally {
    setIsLoading(false);
  }
};


  const handleUpdateFamily = async () => {
    if (!selectedFamily) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/students/family/update`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          id: selectedFamily.id,
          firstname: selectedFamily.firstname,
          email: selectedFamily.email
        })
      });
      if (!response.ok) {
        throw new Error('Failed to update family');
      }
      fetchStudents(selectedSchoolId, currentPage, studentsPerPage, debouncedSearchQuery);
      setIsFamilyModalOpen(false);
      toast({
        title: "Family updated",
        description: "The family has been updated successfully."
      });
      setHasNameChanged(false);
      setHasEmailChanged(false);
      setInitialFamily({ ...selectedFamily });
    } catch (error) {
      console.error('Error updating family:', error);
      toast({
        title: "Failed to update family",
        description: "There was an error updating the family. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleStudentSelection = (studentId: string) => {
    setSelectedStudents(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const toggleSelectAll = () => {
  const currentPageStudentIds = students
    .filter(student => student.status !== 'deleted' && isStudentFromCurrentSchool(student))
    .map(student => student.id);

  if (currentPageStudentIds.length === 0) return;

  if (currentPageStudentIds.every(id => selectedStudents.includes(id))) {
    setSelectedStudents(prev =>
      prev.filter(id => !currentPageStudentIds.includes(id))
    );
  } else {
    const newSelected = [...selectedStudents];
    currentPageStudentIds.forEach(id => {
      if (!newSelected.includes(id)) {
        newSelected.push(id);
      }
    });
    setSelectedStudents(newSelected);
  }
};


  const deleteSelectedStudents = async () => {
    const studentsToDelete = selectedStudents.filter(id =>
      students.some(s => s.id === id && isStudentFromCurrentSchool(s))
    );
    if (studentsToDelete.length === 0) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/students/deleteSelected`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ids: studentsToDelete })
      });
      if (!response.ok) {
        throw new Error('Failed to delete student');
      }
      setSelectedStudents([]);
      setIsConfirmDeleteOpen(false);
      fetchStudents(selectedSchoolId, currentPage, studentsPerPage, debouncedSearchQuery);
      toast({
        title: "Student deleted",
      });
    } catch (error) {
      console.error('Error deleting student:', error);
      toast({
        title: "Failed to delete student",
        description: "There was an error deleting the student. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };


  const handleCreateFamily = async () => {
    if (!newFamilyName.trim()) {
      toast({
        title: "Family name is required",
        description: "Please enter a name for the family.",
        variant: "destructive"
      });
      return;
    }
    if (!newFamilyEmail.trim()) {
      toast({
        title: "Family email is required",
        description: "Please enter a email for the family.",
        variant: "destructive"
      });
      return;
    }
    if (selectedStudentsForFamily.length === 0) {
      toast({
        title: "No students selected",
        description: "Please select at least one student for the family.",
        variant: "destructive"
      });
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/students/families/create`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newFamilyName.trim(),
          email: newFamilyEmail.trim(),
          studentIds: selectedStudentsForFamily
        })
      });
      if (!response.ok) {
        throw new Error('Failed to create family');
      }
      toast({
        title: "Family created",
        description: `${newFamilyName} has been created successfully.`
      });
      fetchStudents(selectedSchoolId, currentPage, studentsPerPage, debouncedSearchQuery);
      setIsCreateFamilyModalOpen(false);
      setNewFamilyName('');
      setNewFamilyEmail('');
      setSelectedStudentsForFamily([]);
    } catch (error) {
      console.error('Error creating family:', error);
      toast({
        title: "Failed to create family",
        description: "There was an error creating the family. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFamilyClick = (family: Family) => {
    setSelectedFamily(family);
    setIsFamilyModalOpen(true);
  };

  const handleDeleteFamily = async (studentId: string, familyId: number) => {
    try {
      const response = await fetch(`${BASE_URL}/api/students/dissociate-family`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ studentId, familyId })
      });
      if (!response.ok) {
        throw new Error('Failed to delete family');
      }
      fetchStudents(selectedSchoolId, currentPage, studentsPerPage, debouncedSearchQuery);
      if (selectedFamily && selectedFamily.id === familyId) {
        const updatedFamilies = selectedFamily.students.filter(student => student.id !== studentId);
        selectedFamily.students = updatedFamilies;
      }
      toast({
        title: "Student dissociated",
        description: `The student has been dissociated from the family.`,
      });
    } catch (error) {
      console.error('Error deleting family:', error);
      toast({
        title: "Failed to dissociate student",
        description: "There was an error dissociating the student from the family.",
        variant: "destructive"
      });
    }
  };

  const handleAddStudent = async (firstname: string, lastname: string, email: string, isInvite: boolean) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/add_student`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          firstname: firstname.trim(),
          lastname: lastname.trim(),
          email: email.trim(),
          isInvite: isInvite,
          schoolId: selectedSchoolId
        })
      });
      if (!response.ok) {
        throw new Error('Failed to add student');
      }
      fetchStudents(selectedSchoolId, currentPage, studentsPerPage, debouncedSearchQuery);
      // Refresh user data so wizard flags (hasCreatedStudent) update across UI
      try { await refreshUserData?.(); } catch {}
      toast({
        title: t('students.msgStudentAddedTitle'),
        description: t('students.msgStudentAdded', { firstname: firstname, lastname: lastname }),
      });
      setIsAddingStudent(false);
    } catch (error) {
      console.error('Error adding student:', error);
      toast({
        title: "Failed to add student",
        description: "There was an error adding the student. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStudent = async (formData: FormData) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/student/update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update student: ${errorText}`);
      }
      const updatedStudentData = await response.json();
      fetchStudents(selectedSchoolId, currentPage, studentsPerPage, debouncedSearchQuery);
      eventService.clearEventsCache();
      queryClient.clear();

      toast({
        title: "Student updated",
        description: `${formData.get('firstname')} ${formData.get('lastname')} has been updated`
      });
      setEditingStudent(null);
    } catch (error) {
      console.error('Error updating student:', error);
      toast({
        title: "Failed to update student",
        description: error.message || "There was an error updating the student. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenContactFromEdit = (studentToContact: Student) => {
    setEditingStudent(null); // Ferme le modal d'édition
    setSelectedStudentForContact(studentToContact); // Prépare les données pour le modal de contact
    setIsContactModalOpen(true); // Ouvre le modal de contact
    setShouldReopenEditModal(true); // Mémorise qu'il faudra rouvrir le modal d'édition
  };

  const handleCloseContactModal = () => {
    setIsContactModalOpen(false);
    if (shouldReopenEditModal && selectedStudentForContact) {
      setEditingStudent(selectedStudentForContact); // Rouvre le modal d'édition
      setShouldReopenEditModal(false); // Réinitialise le flag
    }
    setSelectedStudentForContact(null);
  };

  const sendInvite = async (studentId: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/invite/student`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ studentId })
      });
      if (!response.ok) throw new Error('Failed to send invite');
      toast({ title: "Invitation sent", description: "The invitation has been sent successfully." });

      // Rafraîchir la liste et récupérer les nouveaux étudiants
      const newStudents = await fetchStudents(selectedSchoolId, currentPage, studentsPerPage, debouncedSearchQuery);

      // Trouver le student mis à jour dans la nouvelle liste
      const updatedStudent = newStudents.find(s => s.id == studentId);
      if (updatedStudent) {
        setEditingStudent({ ...updatedStudent });
        setEditKey(prev => prev + 1); // Force le re-rendu de EditStudentForm
      }
    } catch (error) {
      console.error('Error sending invite:', error);
      toast({ title: "Failed to send invite", description: "Please try again.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };


  const sendAccountInformation = async (studentId: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/invite/password`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ studentId })
      });
      if (!response.ok) {
        throw new Error('Failed to send invite');
      }
      toast({
        title: "Invitation sent",
        description: "The invitation has been sent successfully."
      });
    } catch (error) {
      console.error('Error sending invite:', error);
      toast({
        title: "Failed to send invite",
        description: "There was an error sending the invitation. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const inviteToCreateAccount = async (id: number) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/invite/parent`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id })
      });
      if (!response.ok) {
        throw new Error('Failed to send invite');
      }
      setIsFamilyInvited(true);
      toast({
        title: "Invitation sent",
        description: "The invitation has been sent successfully."
      });
    } catch (error) {
      console.error('Error sending invite:', error);
      toast({
        title: "Failed to send invite",
        description: "There was an error sending the invitation. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        setIsFamilyInvited(false);
      }, 2600);
    }
  };

  const sendParentAccountInformation = async (familyId) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/invite/parent/password`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ familyId })
      });
      if (!response.ok) {
        throw new Error('Failed to send invite');
      }
      setIsFamilyInvited(true);
      toast({
        title: "Invitation sent",
        description: "The invitation has been sent successfully."
      });
    } catch (error) {
      console.error('Error sending invite:', error);
      toast({
        title: "Failed to send invite",
        description: "There was an error sending the invitation. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        setIsFamilyInvited(false);
      }, 2600);
    }
  };

  const handleDeleteStudent = (student: Student) => {
    setStudentToDelete(student);
    setIsSingleDeleteConfirmOpen(true);
  };

  const confirmDeleteStudent = async () => {
    if (!studentToDelete) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/delete_student`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id: studentToDelete.id })
      });
      if (!response.ok) {
        throw new Error('Failed to delete student');
      }

      // If the deleted student was the last one on the current page (and it's not page 1),
      // go to the previous page.
      const activeStudentsOnPage = students.filter(s => s.status !== 'deleted').length;
      if (activeStudentsOnPage === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchStudents(selectedSchoolId, currentPage, studentsPerPage, debouncedSearchQuery);
      }

      toast({
        title: t('students.msgStudentDeletedTitle'),
        description: t('students.msgConfirmDeletedStudent', { firstname: studentToDelete.firstname, lastname: studentToDelete.lastname }),
      });
    } catch (error) {
      console.error('Error deleting student:', error);
      toast({
        title: "Failed to delete student",
        description: "There was an error deleting the student. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
      setIsSingleDeleteConfirmOpen(false);
      setStudentToDelete(null);
    }
  };

  const isMySchool = () => {
    return user.school_id == selectedSchoolId;
  };

  const isStudentFromCurrentSchool = (student: Student = null) => {
    return user.school_id == selectedSchoolId;
  };

  const extractFamilies = (students: Student[]) => {
    const familiesMap: Record<number, Family> = {};
    students.forEach(student => {
      if (student.families) {
        student.families.forEach(family => {
          if (!familiesMap[family.id]) {
            familiesMap[family.id] = {
              id: family.id,
              firstname: family.firstname,
              email: family.email,
              isUserAccount: family.isUserAccount,
              students: [],
              studentCount: 0,
            };
          }
          familiesMap[family.id].students.push({
            id: student.id,
            firstname: student.firstname,
            lastname: student.lastname,
          });
        });
      }
    });
    const familiesList = Object.values(familiesMap);
    familiesList.forEach((family: Family) => {
      family.studentCount = family.students.length;
    });
    return familiesList;
  };

  //const families = extractFamilies(students);
  const filteredFamilies = families.filter((family: Family) => {
    const fullName = `${family.firstname}`.toLowerCase();
    return fullName.includes(familySearchQuery.toLowerCase());
  });

  const navigateToStudentDetail = (studentId: string) => {
    navigate(`/student/${studentId}`);
  };

  const exportToExcel = async () => {
    setIsLoading(true);
    try {
      // First, fetch all students for the selected school
      const response = await fetch(`${BASE_URL}/api/students/school/${selectedSchoolId}?page=1&per_page=${totalResults}`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch students for export');
      }

      const data = await response.json();
      const studentsToExport = data.students || [];

      if (studentsToExport.length === 0) {
        toast({
          title: "No students to export",
          description: "There are no students to export.",
        });
        return;
      }

      const activeStudents = studentsToExport.filter(student => student.status !== 'deleted');
      const dataToExport = activeStudents.map(student => ({
        firstname: student.firstname,
        lastname: student.lastname,
        email: student.email || '',
        phone: student.phone || '',
        mobile: student.mobile || '',
        mother_email: student.mother_email || '',
        mother_phone: student.mother_phone || '',
        father_email: student.father_email || '',
        father_phone: student.father_phone || '',
      }));
      const ws = utils.json_to_sheet(dataToExport);
      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, "Students");
      const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `students_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast({
        title: "Export successful",
        description: `${dataToExport.length} students exported to Excel file`,
      });
    } catch (error) {
      console.error('Error exporting data:', error);
      toast({
        title: "Export failed",
        description: "There was an error exporting the students data.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const duplicateDetectorRef = useRef<DuplicateStudentsDetectorHandle>(null);

  const handleOpenMergeModal = (student: Student) => {
    if (!isStudentFromCurrentSchool(student)) return;
    duplicateDetectorRef.current?.openMergeModal(student);
  };

  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = isMobile ? 3 : 5;
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        items.push(
          <PaginationItem key={i}>
            <PaginationLink
              isActive={currentPage === i}
              onClick={() => setCurrentPage(i)}
              className='cursor-pointer'
            >
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }
    } else {
      items.push(
        <PaginationItem key={1}>
          <PaginationLink
            isActive={currentPage === 1}
            onClick={() => setCurrentPage(1)}
            className='cursor-pointer'
          >
            1
          </PaginationLink>
        </PaginationItem>
      );
      let startPage, endPage;
      if (isMobile) {
        if (currentPage > 2) {
          items.push(
            <PaginationItem key="ellipsis-1">
              <PaginationEllipsis />
            </PaginationItem>
          );
        }
        if (currentPage !== 1 && currentPage !== totalPages) {
          items.push(
            <PaginationItem key={currentPage}>
              <PaginationLink isActive={true}>
                {currentPage}
              </PaginationLink>
            </PaginationItem>
          );
        }
        if (currentPage < totalPages - 1) {
          items.push(
            <PaginationItem key="ellipsis-2">
              <PaginationEllipsis />
            </PaginationItem>
          );
        }
      } else {
        startPage = Math.max(2, currentPage - 1);
        endPage = Math.min(startPage + 2, totalPages - 1);
        if (endPage === totalPages - 1) {
          startPage = Math.max(2, endPage - 2);
        }
        if (startPage > 2) {
          items.push(
            <PaginationItem key="ellipsis-1">
              <PaginationEllipsis />
            </PaginationItem>
          );
        }
        for (let i = startPage; i <= endPage; i++) {
          items.push(
            <PaginationItem key={i}>
              <PaginationLink
                isActive={currentPage === i}
                onClick={() => setCurrentPage(i)}
                className='cursor-pointer'
              >
                {i}
              </PaginationLink>
            </PaginationItem>
          );
        }
        if (endPage < totalPages - 1) {
          items.push(
            <PaginationItem key="ellipsis-2">
              <PaginationEllipsis />
            </PaginationItem>
          );
        }
      }
      items.push(
        <PaginationItem key={totalPages}>
          <PaginationLink
            isActive={currentPage === totalPages}
            onClick={() => setCurrentPage(totalPages)}
            className='cursor-pointer'
          >
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }
    return items;
  };

  const handleImportData = (data: any) => {
    setImportedData(data);
    const initialMappings = data.headers.reduce((acc: Record<string, string>, header: string) => {
      acc[header] = '';
      return acc;
    }, {});
    setFieldMappings(initialMappings);
  };

  const handleFieldMappingChange = (header: string, value: string) => {
    setFieldMappings(prevMappings => ({
      ...prevMappings,
      [header]: value
    }));
  };

  const fieldOptions = [
    "Lastname",
    "Firstname",
    "Email",
    "Phone",
    "Mobile",
    "Mother's Email",
    "Mother's Phone",
    "Father's Email",
    "Father's Phone",
  ];

  const handleValidateMappings = async () => {
    const mappedStudents = importedData.sample.map((row: string[]) => {
      const studentData: Record<string, string> = {};
      importedData.headers.forEach((header: string) => {
        if (fieldMappings[header]) {
          studentData[fieldMappings[header].toLowerCase()] = row[importedData.headers.indexOf(header)];
        }
      });
      return studentData;
    });
    try {
      const response = await fetch(`${BASE_URL}/api/add_students`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          students: mappedStudents,
          schoolId: selectedSchoolId
        })
      });
      if (!response.ok) {
        throw new Error('Failed to add students');
      }
      const data = await response.json();
      toast({
        title: t('students.msgStudentAddedTitle'),
        description: t('students.msgStudentsAdded', {count: mappedStudents.length}),
      });
      fetchStudents(selectedSchoolId, currentPage, studentsPerPage, debouncedSearchQuery);
      setImportedData(null);
    } catch (error) {
      console.error('Error adding students:', error);
      toast({
        title: "Failed to add students",
        description: "There was an error adding the students. Please try again.",
        variant: "destructive"
      });
    }
  };

  const areAllCurrentPageSelected = () => {
    const currentPageStudentIds = students
      .filter(student => student.status !== 'deleted' && isStudentFromCurrentSchool(student))
      .map(student => student.id);
    return currentPageStudentIds.length > 0 &&
          currentPageStudentIds.every(id => selectedStudents.includes(id));
  };


  const activeStudentCount = students.filter(student => student.status !== 'deleted').length;

  const CardComponent = small ? React.Fragment : Card;
  const extraProps = small ? {} : { className: "mb-6" };

  return (
    <CardComponent {...extraProps}>

      {/*user?.account_type === "C" && user?.roleType === "teachers_all" && user?.schools.length > 1 && !isLoadingStudents && isDuplicateReady && (
      <DuplicateStudentsDetector
        ref={duplicateDetectorRef}
        onStudentMerged={() => fetchStudents(selectedSchoolId, currentPage, studentsPerPage, debouncedSearchQuery)}
        isDuplicateLoading={() => setisDuplicateLoading(true)}
        onDuplicatesDetected={(count) => {
          setDuplicatesCount(count);
          setDuplicatesDetected(count > 0);
        }}
      />
      )*/}

      {!small && (
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <Users className="h-5 w-5" />
            {!isViewingFamilies ? t('students.students') : t('students.families')}
          </CardTitle>
        </CardHeader>
      )}
      <CardContent className={small ? "p-0" : ""}>
        
        {importedData ? (
          <div>
            <div className="rounded-md border overflow-hidden">
              <div className="w-full overflow-x-auto">
                <table className="min-w-full bg-white">
                  <thead className="bg-muted/50">
                    <tr className="text-left">
                      {importedData.headers.map((header: string, index: number) => (
                        <th key={index} className="p-3 font-medium">
                          <p>{header}</p>
                          <select
                            value={fieldMappings[header]}
                            onChange={(e) => handleFieldMappingChange(header, e.target.value)}
                            className="p-1 border rounded"
                          >
                            <option value="">Select field</option>
                            {fieldOptions.map(option => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {importedData.sample.map((row: string[], rowIndex: number) => (
                      <tr key={rowIndex}>
                        {row.map((cell: string, cellIndex: number) => (
                          <td key={cellIndex} className="py-2 px-4 border-b">{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="flex flex-row justify-center sm:justify-end space-x-2 mt-4">
              <Button variant="outline" onClick={() => setImportedData(null)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleValidateMappings}>
                {t('common.done')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 mb-4 rounded-lg border bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950 p-3 md:p-4">
              {userSchools.length > 1  && !isLoadingStudents && (
                <Select
                  value={selectedSchoolId}
                  onValueChange={setSelectedSchoolId}
                >
                  <SelectTrigger className="w-full sm:w-auto md:w-[280px] text-base">
                    <SelectValue>
                      {(() => {
                        const selectedSchool = userSchools.find(school => school.id == selectedSchoolId);
                        return selectedSchool ? (
                          <span className='flex items-center gap-2'>
                            {selectedSchool.school_type === 'C' ? (
                              <GraduationCap className="inline-block h-4 w-4" />
                            ) : (
                              <Building2 className="inline-block h-4 w-4" />
                            )}
                            {selectedSchool.school_name}
                          </span>
                        ) : (
                          "Select a school"
                        );
                      })()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {userSchools.map(school => (
                      <SelectItem key={school.id} value={school.id}>
                        {school.school_type === 'C' ? (
                          <>
                            <GraduationCap className="inline-block h-4 w-4 mr-2" />
                            {school.school_name} ({t('students.coachStudents')})
                          </>
                        ) : (
                          <>
                            <Building2 className="inline-block h-4 w-4 mr-2" />
                            {school.school_name} ({t('students.schoolStudents')})
                          </>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              <div className="relative w-full sm:flex-grow sm:max-w-[200px]">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={searchInputRef}
                  type="text"
                  placeholder={isViewingFamilies ? t('students.search_families') : t('students.search_students')}
                  value={isViewingFamilies ? familySearchQuery : searchQuery}
                  onChange={(e) => isViewingFamilies ? setFamilySearchQuery(e.target.value) : setSearchQuery(e.target.value)}
                  className="pl-8 w-full"
                />
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto sm:ml-auto justify-end flex-wrap">

                {selectedStudents.length === 0 && (
                  <>
                <Button
                  variant="outline"
                  onClick={() => setIsViewingFamilies(!isViewingFamilies)}
                  className="gap-2 flex-1 sm:flex-none"
                >
                  {!isViewingFamilies ? (
                    <>
                      <Users className="h-4 w-4" />
                      <span className="hidden md:inline">{t('students.families')}</span>
                    </>
                  ) : (
                    <>
                      <ChevronLeft className="h-4 w-4" />
                      <span className="hidden md:inline">{"Students"}</span>
                    </>
                  )}
                </Button>


                {!isViewingFamilies && isMySchool() && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        className="gap-2 flex-1 sm:flex-none"
                        disabled={isLoading}
                      >
                        <FileUserIcon className="h-4 w-4" />
 
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setIsImportModalOpen(true)}>
                        <Upload className="mr-2 h-4 w-4" />
                        {t('students.import.import') || 'Import'}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={exportToExcel}
                        disabled={isLoading || students.length === 0}
                      >
                        <FileCode className="mr-2 h-4 w-4" />
                        {'Export'}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {isViewingFamilies && isMySchool() ? (
                  <Button
                    variant="default"
                    className="gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 flex-1 sm:flex-none"
                    onClick={async () => {
                      await fetchAllStudents();
                      setIsCreateFamilyModalOpen(true);
                    }}
                    disabled={isLoadingAllStudents}
                  >
                    {isLoadingAllStudents ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                     <span className="hidden md:inline">{t('students.addFamily')}</span>
                  </Button>
                ) : isMySchool() ? (
                  <Button
                    id="settings-students-add-btn"
                    variant="default"
                    className="gap-2 bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white flex-1 sm:flex-none"
                    onClick={() => setIsAddingStudent(true)}
                  >
                    <Plus className="h-4 w-4" />
                    <span>{t('students.add')}</span>
                  </Button>
                ) : null}
                </>
                )}

                {selectedStudents.length > 0 && (
                  <Button
                    variant="destructive"
                    onClick={() => setIsConfirmDeleteOpen(true)}
                    className="gap-2 flex-1 sm:flex-none"
                    disabled={
                      isLoading ||
                      selectedStudents.some(id =>
                        !students.find(s => s.id === id && isStudentFromCurrentSchool(s))
                      )
                    }
                  >
                    <Trash className="h-4 w-4" />
                    <span>{`${t('common.delete')} (${selectedStudents.length})`}</span>
                  </Button>
                )}
              </div>
            </div>

            {isLoadingStudents ? (
              <div className="text-center py-8">
                <div className="inline-flex items-center gap-2">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin inline-block" />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {activeStudentCount > 0 && !isViewingFamilies && (
                  <div className="flex items-center gap-4 p-3 bg-muted/30 rounded-lg border">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="select-all"
                        checked={areAllCurrentPageSelected()}
                        onCheckedChange={toggleSelectAll}
                      />
                      <label htmlFor="select-all" className="text-sm font-medium cursor-pointer select-none">
                        {areAllCurrentPageSelected() ? t('common.deselectAll') : t('common.selectAll')}
                      </label>
                    </div>
                  </div>
                )}

                <div className="space-y-1">
                  {isViewingFamilies ? (
                    filteredFamilies.map((family: Family, index: number) => (
                      <div
                        key={index}
                        className={`flex items-center p-3 rounded-md transition-all cursor-pointer hover:bg-slate-100 ${
                          index % 2 === 0 ? 'bg-slate-50' : 'bg-slate-100'
                        }`}
                        onClick={() => handleFamilyClick(family)}
                      >
                        <div className="flex items-center flex-1">
                          <div className="flex items-center gap-3">
                            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                              <User className="h-4 w-4 text-primary" />
                            </div>
                            <div className="font-medium text-foreground">
                              {family.firstname}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFamilyClick(family);
                            }}
                            className="h-7 w-7 text-muted-foreground hover:text-foreground"
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>

                            <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteFamily(family.id);
                            }}
                            className="h-7 w-7 text-destructive hover:text-destructive"
                          >
                            <Trash className="h-3 w-3" />
                          </Button>

                        </div>
                      </div>
                    ))
                  ) : (
                    students
                    .filter(student => student.status !== 'deleted')
                    .map((student: Student, index: number) => (
                      <div
                        key={index}
                        className={`flex items-center p-3 rounded-md transition-all hover:bg-slate-200 ${
                          index % 2 === 0 ? 'bg-slate-50' : 'bg-slate-100'
                        }`}
                      >
                        <div className="flex items-center flex-1">
                          <div className="flex items-center gap-3">
                            <Checkbox
                              id={`student-${student.id}`}
                              checked={selectedStudents.includes(student.id)}
                              onCheckedChange={() => toggleStudentSelection(student.id)}
                              disabled={!isStudentFromCurrentSchool(student)}
                            />
                            
                            <div
                              className="flex items-center gap-3 cursor-pointer hover:text-primary transition-colors"
                              onClick={() => setEditingStudent(student)}
                            >
                              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                {student.families?.length > 0 ? (
                                  student.has_user_account ? (
                                    <UserCheck2 className="h-3 w-3 text-primary" />
                                  ) : (
                                    <Users2 className="h-3 w-3 text-primary opacity-50" />
                                  )
                                ) : (
                                  student.has_user_account ? (
                                    <UserCheck className="h-3 w-3 text-primary" />
                                  ) : (
                                    <User className="h-3 w-3 text-primary opacity-50" />
                                  )
                                )}
                              </div>
                              
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={isMobile ? "text-sm" : "font-medium text-foreground"}>
                                  {student.firstname} {student.lastname}
                                </span>
                                
                                {/* Badges des écoles sur la même ligne */}
                                {/*student.schools && student.schools.length > 1 && student.schools.map((school) => (
                                  <Badge key={school.id} variant="secondary" className="text-xs h-5">
                                    {school.school_type === 'S' ? (
                                      <Building2 className="w-2.5 h-2.5 mr-1" />
                                    ) : (
                                      <UserCheck className="w-2.5 h-2.5 mr-1" />
                                    )}
                                    {school.school_name}
                                  </Badge>
                                ))*/}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex gap-1 shrink-0">
                          {!isMobile && user?.account_type === "C" && isStudentFromCurrentSchool(student) && (
                           <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              if (student.email) {
                                setSelectedStudentForContact(student);
                                setIsContactModalOpen(true);
                              }
                            }}
                            disabled={!student.email}
                            title={!student.email ? t('students.noEmailForContact') : t('students.contactStudent')}
                            className={`h-7 w-7 text-blue-500 hover:text-blue-600 ${isMobile ? 'hidden' : ''} ${!student.email ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <Mail className="h-3 w-3" />
                          </Button>
                          )}

                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditingStudent(student)}
                            className="h-7 w-7 text-muted-foreground hover:text-foreground"
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>

                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteStudent(student)}
                            disabled={!isStudentFromCurrentSchool(student)}
                            className="h-7 w-7 text-destructive hover:text-destructive"
                          >
                            <Trash className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                  
                  {students.length === 0 && !isLoadingStudents && (
                    <div className="text-center p-8 text-muted-foreground">
                      <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
                        <User className="h-8 w-8" />
                      </div>
                      <div className="font-medium mb-1">
                        {searchQuery ? t('students.noMatchingStudentFound') : t('students.noStudentAddedYet')}
                      </div>
                      <div className="text-sm">
                        {!searchQuery && isMySchool() && t('students.startByAddingFirstStudent')}
                      </div>
                    </div>
                  )}
                </div>


                {totalResults > 10 && !isViewingFamilies && (
                <div className="flex items-center justify-center gap-2">
                  <Label htmlFor="students-per-page">{t('students.perPage')}</Label>
                  <Select
                    value={studentsPerPage.toString()}
                    onValueChange={(value) => {
                      setStudentsPerPage(Number(value));
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className="w-[80px]">
                      <SelectValue placeholder={studentsPerPage} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                )}

                {totalPages > 1 && !isLoadingStudents && !isViewingFamilies && (
                  <Pagination className="mt-6">
                    <PaginationContent className="px-1">
                      <PaginationItem>
                        <PaginationPrevious
                          onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                          className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        />
                      </PaginationItem>
                      {renderPaginationItems()}
                      <PaginationItem>
                        <PaginationNext
                          onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                          className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                )}
              </div>
            )}
          </div>
        )}
        
        <Dialog open={!!editingStudent} onOpenChange={(open) => !open && setEditingStudent(null)}>
          <DialogContent className={isMobile ? "w-full h-[100dvh] max-h-[100dvh] flex flex-col" : "max-w-[600px] max-h-[80vh] flex flex-col"}>
            <DialogHeader className={isMobile ? "px-6 py-4 border-b mt-0 bg-gray-50" : "pb-5"}>
              <div className="flex items-center gap-2 font-medium text-lg">
                <UserCog className="h-5 w-5 text-blue-500" />
                {t('students.edit')}
              </div>
            </DialogHeader>
            {editingStudent && (
              <div className="flex-1 overflow-auto">
                <EditStudentForm
                  key={editKey}
                  student={editingStudent}
                  onSubmit={handleUpdateStudent}
                  onCancel={() => setEditingStudent(null)}
                  sendInvite={sendInvite}
                  sendAccountInformation={sendAccountInformation}
                  submitDelete={handleDeleteFamily}
                  onStudentRefresh={() => handleStudentRefresh()}
                  onOpenContact={handleOpenContactFromEdit}
                />
              </div>
            )}
          </DialogContent>
        </Dialog>
        <ImportStudentsModal
          open={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          onImport={handleImportData}
        />
        
        <Dialog open={isFamilyModalOpen} onOpenChange={(open) => !open && setIsFamilyModalOpen(false)}>
          <DialogContent className={isMobile ? "w-full h-[100dvh] max-h-[100dvh] flex flex-col p-0" : "max-w-[650px] max-h-[80vh] flex flex-col p-0"}>
            <DialogHeader className={isMobile ? "px-6 py-4 border-b shrink-0 mt-0" : "px-6 pb-4 pt-6 shrink-0"}>
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-500" />
                <DialogTitle>
                  {showAddStudentsList ? t('students.addStudentTofamily') : t('students.familyDetails')}
                </DialogTitle>
              </div>
            </DialogHeader>

            {selectedFamily && (
              <div className="flex-1 overflow-y-auto px-6 min-h-0">
                {!showAddStudentsList ? (
                  <div className="space-y-6 py-4">
                    {/* Formulaire d'information famille */}
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstname">{t('students.lastName')}</Label>
                        <Input
                          id="firstname"
                          value={selectedFamily?.firstname || ''}
                          onChange={handleNameChange}
                          placeholder={t('students.enterFirstname')}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email">{t('students.email')}</Label>
                        <Input
                          id="email"
                          type="email"
                          value={selectedFamily.email}
                          onChange={handleEmailChange}
                          placeholder={t('students.enterEmail')}
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="font-normal">
                            {selectedFamily.isUserAccount ? t('students.registered') : t('students.noAccount')}
                          </Badge>

                          {selectedFamily.isUserAccount && !isFamilyInvited && (
                            <Button 
                              variant="link" 
                              size="sm"
                              onClick={() => sendParentAccountInformation(selectedFamily.id)}
                              className="h-auto p-0"
                            >
                              {t('students.sendIdentifier')}
                            </Button>
                          )}
                          {selectedFamily.isUserAccount && isFamilyInvited && (
                            <span className="font-normal text-green-600">
                              {t('students.passwordLinkSent')}
                            </span>
                          )}

                          {!selectedFamily.isUserAccount && !isFamilyInvited && (
                            <Button 
                              variant="link" 
                              size="sm"
                              onClick={() => inviteToCreateAccount(selectedFamily.id)}
                              className="h-auto p-0"
                            >
                              {t('students.inviteToCreateAccount')}
                            </Button>
                          )}
                          {!selectedFamily.isUserAccount && isFamilyInvited && (
                            <span className="font-normal text-green-600">
                              {t('students.invitationSent')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Liste des étudiants associés */}
                    <div className="space-y-3">
                      <Label>{t('students.familyMembers')}</Label>
                      {selectedFamily && selectedFamily.students && selectedFamily.students.length > 0 ? (
                        <div className="rounded border overflow-hidden">
                          <div className="max-h-[300px] overflow-y-auto">
                            <table className="min-w-full rounded-sm bg-white">
                              <thead className="bg-gray-50 rounded-md top-0">
                                <tr className="text-left">
                                  <th className="p-3 font-medium text-sm">{t('students.student')}</th>
                                  <th className="p-3 font-medium text-sm text-right">{t('common.actions')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedFamily.students.map((student) => (
                                  <tr key={student.id} className="border-t">
                                    <td className="pt-2 flex items-center">
                                      <User className="h-4 w-4 mr-2 text-muted-foreground pl-1" />
                                      <span className="text-sm">{student.firstname} {student.lastname}</span>
                                    </td>
                                    <td className="text-right">
                                      <TooltipProvider>
                                        <Tooltip delayDuration={0}>
                                          <TooltipTrigger asChild>
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              onClick={() => handleDeleteFamily(student.id, selectedFamily.id)}
                                              className="h-8 w-8 text-destructive hover:text-destructive"
                                            >
                                              <UserMinus className="h-4 w-4" />
                                            </Button>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>{t('students.dissociateStudent')}</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground italic">
                          {t('students.noStudentsAssociated')}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Vue d'ajout d'étudiants */
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="search-students">{t('students.selectStudents')}</Label>
                      <Input
                        id="search-students"
                        type="text"
                        placeholder={t('students.search_students')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>

                    <div className="rounded-md border overflow-hidden">
                      <div className="max-h-[50vh] overflow-y-auto">
                        {allStudents
                          .filter(student =>
                            student.status !== 'deleted' &&
                            !((selectedFamily.students || []).some(s => s.id === student.id)) &&
                            (student.firstname.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            student.lastname.toLowerCase().includes(searchTerm.toLowerCase()))
                          )
                          .map((student) => (
                            <div
                              key={student.id}
                              className={`flex items-center p-3 border-b last:border-b-0 cursor-pointer hover:bg-muted/50 transition-colors ${
                                selectedStudentsToAdd.includes(student.id) ? 'bg-blue-50' : ''
                              }`}
                              onClick={() => {
                                const currentIds = selectedStudentsToAdd;
                                let newIds;
                                if (currentIds.includes(student.id)) {
                                  newIds = currentIds.filter(id => id !== student.id);
                                } else {
                                  newIds = [...currentIds, student.id];
                                }
                                setSelectedStudentsToAdd(newIds);
                              }}
                            >
                              <div className={`h-4 w-4 border rounded-sm mr-3 flex items-center justify-center ${
                                selectedStudentsToAdd.includes(student.id) ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                              }`}>
                                {selectedStudentsToAdd.includes(student.id) && <Check className="h-3 w-3 text-white" />}
                              </div>
                              <span className="flex-1 text-sm">{student.firstname} {student.lastname}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter className={isMobile ? "px-6 py-4 border-t bg-background shrink-0 pb-5" : "pt-4 px-6 pb-6 border-t shrink-0"}>
              {!showAddStudentsList ? (
                <>
                  <Button 
                    variant="outline" 
                    onClick={async () => {
                      await fetchAllStudents();
                      setShowAddStudentsList(true);
                      setSearchTerm('');
                      setSelectedStudentsToAdd([]);
                    }}
                    disabled={isLoadingAllStudents}
                  >
                    {isLoadingAllStudents ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <UserPlus className="h-4 w-4 mr-2" />
                    )}
                    {t('students.addStudentTofamily')}
                  </Button>
                  <Button onClick={handleUpdateFamily} disabled={isLoading}>
                    {isLoading ? (
                      <span className="flex items-center">
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      </span>
                    ) : (
                      t('common.update')
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowAddStudentsList(false);
                      setSearchTerm('');
                      setSelectedStudentsToAdd([]);
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button 
                    onClick={handleAddStudentsToFamily} 
                    disabled={isLoading || selectedStudentsToAdd.length === 0}
                  >
                    {isLoading ? (
                      <span className="flex items-center">
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('common.adding')}
                      </span>
                    ) : (
                      <>
                        {t('students.addSelectedStudents')} ({selectedStudentsToAdd.length})
                      </>
                    )}
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        <Dialog open={isCreateFamilyModalOpen} onOpenChange={(open) => !open && setIsCreateFamilyModalOpen(false)}>
          <DialogContent className={isMobile ? "w-full h-[100dvh] max-h-[100dvh] flex flex-col" : "max-w-[650px] max-h-[85vh] flex flex-col"}>
            <DialogHeader className={isMobile ? "px-6 py-4 border-b flex-shrink-0 mt-0" : "px-6 pt-6 pb-4 border-b flex-shrink-0"}>
              <div className="flex items-center gap-2 font-medium text-lg">
                <Users className="h-5 w-5 text-blue-500" />
                {t('students.createNewFamily')}
              </div>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
              <div className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="family-name">{t('students.family_name')}</Label>
                  <Input
                    id="family-name"
                    value={newFamilyName}
                    onChange={(e) => setNewFamilyName(e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="family-email">{t('students.emailFamily')}</Label>
                  <Input
                    id="family-email"
                    value={newFamilyEmail}
                    onChange={(e) => setNewFamilyEmail(e.target.value)}
                  />
                </div>
                <div className="grid gap-2 bg-gray-50 p-5 border rounded-sm">
                  <Label htmlFor="students">{t('students.selectStudents')}</Label>
                  <div className="grid gap-2 mb-2">
                    <Input
                      placeholder={t('students.search_students')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="max-h-[300px] overflow-y-auto border rounded-md bg-white">
                    {allStudents
                      .filter(student => student.status !== 'deleted' && (student.firstname.toLowerCase().includes(searchTerm.toLowerCase()) || student.lastname.toLowerCase().includes(searchTerm.toLowerCase())))
                      .sort((a, b) => a.firstname.localeCompare(b.firstname))
                      .map((student:Student, index: number) => (
                        <div
                          key={index}
                          className={`flex items-center p-2 border-b last:border-b-0 cursor-pointer hover:bg-gray-100 ${
                            selectedStudentsForFamily.includes(student.id) ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => {
                            const currentIds = selectedStudentsForFamily;
                            let newIds;
                            if (currentIds.includes(student.id)) {
                              newIds = currentIds.filter(id => id !== student.id);
                            } else {
                              newIds = [...currentIds, student.id];
                            }
                            setSelectedStudentsForFamily(newIds);
                          }}
                        >
                          <div className={`h-4 w-4 border rounded-sm mr-2 flex items-center justify-center ${
                            selectedStudentsForFamily.includes(student.id) ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                          }`}>
                            {selectedStudentsForFamily.includes(student.id) && <Check className="h-3 w-3 text-white bg-blue-500 border rounded-sm" />}
                          </div>
                          <span className="flex-1">{student.firstname} {student.lastname}</span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
            
            <DialogFooter className={isMobile ? "px-6 py-4 border-t bg-white flex-shrink-0" : "px-6 py-4 border-t bg-white flex-shrink-0"}>
              <Button variant="outline" onClick={() => setIsCreateFamilyModalOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleCreateFamily} disabled={isLoading}>
                {isLoading ? (
                  <span className="flex items-center">
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    Processing
                  </span>
                ) : (
                  t('students.createFamily')
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog open={isFamilyDeleteConfirmOpen} onOpenChange={setIsFamilyDeleteConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader className={isMobile ? "mt-0" : ""}>
              <AlertDialogTitle>{t('students.confirmDelete')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('students.confirmDeleteFamily')} {familyToDelete?.firstname}?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className='mt-5'>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDeleteFamily}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {t('common.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>


        <AlertDialog open={isSingleDeleteConfirmOpen} onOpenChange={setIsSingleDeleteConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader className={isMobile ? "mt-0" : ""}>
              <AlertDialogTitle>{t('students.confirmDelete')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('students.confirmDeleteStudent')} {studentToDelete?.firstname} {studentToDelete?.lastname}?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className='mt-5'>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDeleteStudent} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                {t('common.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <Dialog open={isAddingStudent} onOpenChange={(open) => {
          if (!open) {
            setIsAddingStudent(false);
            window.dispatchEvent(new CustomEvent('wizard-add-dialog-closed', { detail: { type: 'student' } }));
          } else {
            window.dispatchEvent(new CustomEvent('wizard-add-dialog-opened', { detail: { type: 'student' } }));
          }
        }}>
          <DialogContent className={isMobile ? "p-0 w-full h-[100dvh] max-h-[100dvh]" : "max-w-[600px]"}>
            <DialogHeader className={isMobile ? "px-6 py-4 border-b mt-1" : "pb-5"}>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-primary" />
                {t('students.addStudent')}
              </DialogTitle>
            </DialogHeader>
            <div className='flex-1 overflow-auto p-5'>
              <AddStudentForm onSubmit={handleAddStudent} onCancel={() => {
                setIsAddingStudent(false);
                window.dispatchEvent(new CustomEvent('wizard-add-dialog-closed', { detail: { type: 'student' } }));
              }} />
            </div>
          </DialogContent>
        </Dialog>
        <AlertDialog open={isConfirmDeleteOpen} onOpenChange={setIsConfirmDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader className={isMobile ? "mt-0" : ""}>
              <AlertDialogTitle>{t('students.confirmDelete')}</AlertDialogTitle>
            <AlertDialogDescription>
                {t('students.confirmDeleteDesc', { count: selectedStudents.length })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className='mt-5'>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={deleteSelectedStudents}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {t('common.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {selectedStudentForContact && (
          <ContactStudentModal
            isOpen={isContactModalOpen}
            onClose={handleCloseContactModal}
            studentId={selectedStudentForContact.id}
            studentEmail={selectedStudentForContact.email || ''}
            studentName={`${selectedStudentForContact.firstname} ${selectedStudentForContact.lastname}`}
          />
        )}

      </CardContent>
    </CardComponent>
  );
};

export default StudentsManager;